import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Menu,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Info as InfoIcon,
  ArrowDropDown as DropDownIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import config from '../config';
import { getCategoryColor } from '../theme';
import CertificateListRow from './CertificateListRow';

interface Certificate {
  id: string;
  property_address: string;
  normalized_address?: string;
  flat_number?: string | string[];
  certificate_type: string;
  certificate_type_category?: string;
  issue_date: string;
  expiry_date: string;
  original_filename?: string;
  pdf_filename?: string;
}

interface PendingRenewal {
  id: string;
  provider_name: string;
  property_address: string;
  normalized_address?: string;
  flat_number?: string | string[];
  certificate_type: string;
  issue_date: string;
  expiry_date: string;
  certificate_type_category?: string;
  match_confidence: string;
  match_reason: string;
  created_at: string;
  original_filename?: string;
  pdf_filename?: string;
  existing_certificate?: Certificate;
}

interface RenewalReviewCardProps {
  renewal: PendingRenewal;
  onApprove: (renewalId: string, overrideCertificateId?: string, edits?: Record<string, unknown>) => Promise<void>;
  onReject: (renewalId: string) => Promise<void>;
  token: string;
}

const CERTIFICATE_CATEGORIES = [
  'Gas Certificate',
  'Electrical Certificate',
  'Energy Certificate',
  'Fire Certificate',
  'Smoke Certificate',
  'Emergency Lighting Certificate',
  'Insurance Certificate',
  'Licence Certificate',
  'Deposit Protection Certificate',
  'Other',
];

const parseFlats = (flat: string | string[] | undefined): string[] => {
  if (!flat) return [];
  let arr: any = flat;
  if (!Array.isArray(flat)) {
    try { arr = JSON.parse(flat); } catch { arr = [flat]; }
  }
  return (Array.isArray(arr) ? arr : [arr]).filter(
    (f: string) => f && f !== 'flat_number_not_found' && f.toLowerCase() !== 'n/a' && f.toLowerCase() !== 'whole property'
  );
};

const WHOLE_PROPERTY = 'Whole property';
const ADD_AS_NEW = '__new__';

const RenewalReviewCard: React.FC<RenewalReviewCardProps> = ({ renewal, onApprove, onReject, token }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allCertificates, setAllCertificates] = useState<Certificate[]>([]);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [selectedCertificateId, setSelectedCertificateId] = useState<string>('');
  const [title, setTitle] = useState(renewal.certificate_type || '');
  const [category, setCategory] = useState(
    CERTIFICATE_CATEGORIES.includes(renewal.certificate_type_category || '') ? renewal.certificate_type_category! : 'Other'
  );
  const [address, setAddress] = useState(renewal.normalized_address || renewal.property_address || '');
  const initialFlats = parseFlats(renewal.flat_number);
  const [appliesTo, setAppliesTo] = useState<'whole' | 'flat'>(initialFlats.length > 0 ? 'flat' : 'whole');
  const [flatText, setFlatText] = useState(initialFlats.join(', '));
  const [issueDate, setIssueDate] = useState(renewal.issue_date || '');
  const [expiryDate, setExpiryDate] = useState(renewal.expiry_date || '');
  const [categoryAnchor, setCategoryAnchor] = useState<HTMLElement | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [matchAddress, setMatchAddress] = useState('');
  const [matchFlat, setMatchFlat] = useState('');

  const certAddress = (c: Certificate) => c.normalized_address || c.property_address || '';
  const certFlats = (c: Certificate) => {
    const f = parseFlats(c.flat_number);
    return f.length > 0 ? f : [WHOLE_PROPERTY];
  };
  const addressOptions = Array.from(new Set(allCertificates.map(certAddress).filter(Boolean))).sort();
  const flatOptions = Array.from(new Set(
    allCertificates.filter(c => certAddress(c) === matchAddress).flatMap(certFlats)
  )).sort();
  const certOptions = allCertificates.filter(c => certAddress(c) === matchAddress && certFlats(c).includes(matchFlat));
  const selectedCertificate = allCertificates.find(c => c.id === selectedCertificateId);

  // Address, flat and type follow the certificate being renewed; clearing it restores the AI values
  useEffect(() => {
    const source = selectedCertificate;
    const flats = source ? parseFlats(source.flat_number) : parseFlats(renewal.flat_number);
    const cat = source ? source.certificate_type_category : renewal.certificate_type_category;
    setAddress(source ? certAddress(source) : (renewal.normalized_address || renewal.property_address || ''));
    setAppliesTo(flats.length > 0 ? 'flat' : 'whole');
    setFlatText(flats.join(', '));
    setCategory(CERTIFICATE_CATEGORIES.includes(cat || '') ? cat! : 'Other');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCertificate?.id]);

  const openExistingPdf = async (certificateId: string) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/certificates/${certificateId}/file`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Could not load the PDF');
      window.open(URL.createObjectURL(await res.blob()), '_blank');
    } catch (err: any) {
      setError(err.message || 'Could not open the PDF');
    }
  };

  const openPdf = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/api/pending-renewals/${renewal.id}/file`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Could not load the PDF');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err: any) {
      setError(err.message || 'Could not open the PDF');
    }
  };

  // Fetch all certificates for manual override dropdown
  useEffect(() => {
    fetchAllCertificates();
  }, []);

  const fetchAllCertificates = async () => {
    try {
      setLoadingCertificates(true);
      const response = await fetch(`${config.apiUrl}/api/certificates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch certificates');
      }

      const data = await response.json();
      setAllCertificates(data.certificates || []);
    } catch (err: any) {
      console.error('Error fetching certificates:', err);
    } finally {
      setLoadingCertificates(false);
    }
  };

  const handleApprove = async (override?: string) => {
    try {
      setProcessing(true);
      setError(null);
      const flats = appliesTo === 'flat' ? flatText.split(',').map(f => f.trim()).filter(Boolean) : [];
      await onApprove(renewal.id, override || selectedCertificateId || undefined, {
        certificate_type: title,
        certificate_type_category: category,
        normalized_address: address,
        flat_number: flats,
        issue_date: issueDate,
        expiry_date: expiryDate,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to approve renewal');
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    try {
      setProcessing(true);
      setError(null);
      await onReject(renewal.id);
    } catch (err: any) {
      setError(err.message || 'Failed to reject renewal');
      setProcessing(false);
    }
  };

  return (
    <Card sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {renewal.existing_certificate ? 'Possible renewal' : 'New certificate'} <em>{renewal.original_filename || renewal.pdf_filename || ''}</em> was detected on {new Date(renewal.created_at).toLocaleDateString()}
            {renewal.provider_name && (
              <Typography component="span" variant="body2" color="textSecondary"> (from {renewal.provider_name})</Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button variant="outlined" color="error" size="small" startIcon={<RejectIcon />} onClick={handleReject} disabled={processing}>
              Reject
            </Button>
            {renewal.existing_certificate && (
              <Button variant="contained" color="success" size="small" startIcon={<ApproveIcon />} onClick={() => handleApprove()} disabled={processing || Boolean(selectedCertificate)}>
                {processing ? <CircularProgress size={20} /> : 'Add as renewal'}
              </Button>
            )}
            <Button variant={renewal.existing_certificate ? 'outlined' : 'contained'} color="success" size="small" startIcon={renewal.existing_certificate ? undefined : <ApproveIcon />} onClick={() => handleApprove('new')} disabled={processing || Boolean(selectedCertificate)}>
              {processing && !renewal.existing_certificate ? <CircularProgress size={20} /> : 'Add as new'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        {renewal.existing_certificate && (
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 1.5 }}>
            <strong>Matched to:</strong> {renewal.existing_certificate.certificate_type}
            {parseFlats(renewal.existing_certificate.flat_number).length > 0 && `, ${parseFlats(renewal.existing_certificate.flat_number).join(', ')}`}
            {' '}({renewal.existing_certificate.normalized_address || renewal.existing_certificate.property_address}, expires {renewal.existing_certificate.expiry_date}).<br />
            {renewal.match_reason}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            onClick={openPdf}
            title="Click to open the PDF"
            sx={{
              width: 120, height: 160, flexShrink: 0, border: '1px solid', borderColor: 'divider',
              borderRadius: 1, overflow: 'hidden', cursor: 'pointer', bgcolor: 'grey.100',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '&:hover': { opacity: 0.85, borderColor: 'primary.main' }
            }}
          >
            {previewFailed ? (
              <Typography variant="caption" color="textSecondary">PDF</Typography>
            ) : (
              <img
                src={`${config.apiUrl}/api/pending-renewals/${renewal.id}/preview`}
                alt="Certificate preview"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={() => setPreviewFailed(true)}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ flexShrink: 0 }}>
                <Chip
                  label={category === 'Other' ? 'Other' : category.replace(' Certificate', '')}
                  onClick={(e) => setCategoryAnchor(e.currentTarget)}
                  onDelete={(e) => setCategoryAnchor((e.currentTarget as HTMLElement).parentElement)}
                  deleteIcon={<DropDownIcon />}
                  disabled={processing || Boolean(selectedCertificate)}
                  size="small"
                  sx={{ backgroundColor: getCategoryColor(category), color: 'white', fontWeight: 600, '& .MuiChip-deleteIcon': { color: 'white' } }}
                />
                <Menu anchorEl={categoryAnchor} open={Boolean(categoryAnchor)} onClose={() => setCategoryAnchor(null)}>
                  {CERTIFICATE_CATEGORIES.map((c) => (
                    <MenuItem key={c} selected={c === category} onClick={() => { setCategory(c); setCategoryAnchor(null); }}>
                      {c}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
              <TextField
                variant="standard"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={processing}
                placeholder="Title"
                sx={{ flex: 1 }}
                InputProps={{ sx: { fontSize: '1.05rem', fontWeight: 600 } }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'center', gap: 1.5 }}>
              <TextField
                variant="standard"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={processing || Boolean(selectedCertificate)}
                placeholder="Property address"
                InputProps={{ startAdornment: <LocationIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={appliesTo}
                onChange={(_, v) => v && setAppliesTo(v)}
                disabled={processing || Boolean(selectedCertificate)}
              >
                <ToggleButton value="whole" sx={{ textTransform: 'none', py: 0.25 }}>Whole property</ToggleButton>
                <ToggleButton value="flat" sx={{ textTransform: 'none', py: 0.25 }}>Flat</ToggleButton>
              </ToggleButtonGroup>
              {appliesTo === 'flat' && (
                <TextField
                  variant="standard"
                  size="small"
                  placeholder="e.g. Flat 3"
                  value={flatText}
                  onChange={(e) => setFlatText(e.target.value)}
                  disabled={processing || Boolean(selectedCertificate)}
                  sx={{ width: 140 }}
                />
              )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Issued" type="date" variant="standard" size="small"
                value={issueDate} onChange={(e) => setIssueDate(e.target.value)}
                InputLabelProps={{ shrink: true }} disabled={processing}
              />
              <TextField
                label="Expires" type="date" variant="standard" size="small"
                value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
                InputLabelProps={{ shrink: true }} disabled={processing}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'info.50', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
            {renewal.existing_certificate
              ? 'We matched this to an existing certificate. If that is wrong, pick the certificate it renews below, or add it as a new one.'
              : 'If this certificate renews an existing one, select it below. Otherwise, add it as a new certificate.'}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1.5fr' }, gap: 1.5 }}>
            <FormControl size="small" disabled={loadingCertificates || processing}>
              <InputLabel>1. Property</InputLabel>
              <Select
                value={matchAddress}
                label="1. Property"
                onChange={(e) => {
                  const v = e.target.value as string;
                  setMatchAddress(v);
                  setMatchFlat('');
                  setSelectedCertificateId(v === ADD_AS_NEW ? 'new' : '');
                }}
              >
                <MenuItem value="">
                  <em>{renewal.existing_certificate ? 'Use suggested match' : 'None - add as new certificate'}</em>
                </MenuItem>
                {renewal.existing_certificate && (
                  <MenuItem value={ADD_AS_NEW}><strong>Add as new certificate</strong></MenuItem>
                )}
                {addressOptions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" disabled={!matchAddress || matchAddress === ADD_AS_NEW || processing}>
              <InputLabel>2. Flat</InputLabel>
              <Select
                value={matchFlat}
                label="2. Flat"
                onChange={(e) => { setMatchFlat(e.target.value as string); setSelectedCertificateId(''); }}
              >
                {flatOptions.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" disabled={!matchFlat || processing}>
              <InputLabel>3. Certificate to renew</InputLabel>
              <Select
                value={selectedCertificateId}
                label="3. Certificate to renew"
                onChange={(e) => setSelectedCertificateId(e.target.value as string)}
                renderValue={(id) => {
                  const c = allCertificates.find(x => x.id === id);
                  return c ? `${c.certificate_type} • expires ${c.expiry_date || 'N/A'}` : '';
                }}
              >
                {certOptions.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }} noWrap>{c.certificate_type}</Typography>
                      {c.certificate_type_category && (
                        <Chip
                          size="small"
                          label={c.certificate_type_category.replace(' Certificate', '')}
                          sx={{ backgroundColor: getCategoryColor(c.certificate_type_category), color: 'white', height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      <Typography variant="caption" color="textSecondary">Expires {c.expiry_date || 'N/A'}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {loadingCertificates && <FormHelperText>Loading certificates...</FormHelperText>}

          {selectedCertificate && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                The certificate that will be replaced:
              </Typography>
              <CertificateListRow
                certificate={selectedCertificate}
                onView={openExistingPdf}
                onReminderError={setError}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => { setMatchAddress(''); setMatchFlat(''); setSelectedCertificateId(''); }}
                  disabled={processing}
                >
                  Clear selection
                </Button>
                <Button variant="contained" color="primary" size="small" onClick={() => handleApprove()} disabled={processing}>
                  {processing ? <CircularProgress size={20} /> : 'Renew this certificate'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RenewalReviewCard;
